import Container from '@atoms/Container';
import Layout from '@components/Layout';
import { useLocator } from '@contexts/franchise/locator-provider.tsx';
import { useMetroPageSections } from '@hooks/use-metro-page-sections';
import paddingDefault from '@utils/paddingDefault';
import RichTextEditorStyles from '@utils/RichTextEditorStyles';
import { graphql } from 'gatsby';
import haversine from 'haversine-distance';
import React from 'react';
import FadeTransition from '../components/animations/transitions/fade-transition';
import Blogroll from '../components/blocks/Blogroll';
import SplitHero from '../components/blocks/hero/split-hero';
import NearestItem from '../components/franchise/nearest-item';
import { colors } from '../components/primitives/colors';
import Divider from '../components/primitives/divider';
import Col from '../components/primitives/grid/col';
import Row from '../components/primitives/grid/row';
import RowSet from '../components/primitives/grid/row-set';
import { StyledPillButtonAsLink } from '../components/primitives/styled-pill-button';
import Typography from '../components/primitives/typography';
import Seo from '../components/utils/Seo';
import { CHANGE_LOCATION } from '../constants/constants';
import CtaProvider from '../contexts/franchise/cta-provider';

const MetroPage = ({ data }) => {
    const { geo, nearby } = useLocator();

    const {
        cmsMetroPage: {
            franchises, hero, page_content, seo, title, location,
        },
    } = data;

    const [nearbyToUse, setNearbyToUse] = React.useState(franchises);
    const [wasNearbyUpdatedDueToUserProximity, setWasNearbyUpdatedDueToUserProximity] = React.useState(false);

    const { blogroll } = useMetroPageSections();

    React.useEffect(() => {
        if (geo && nearby.length && nearby.every(x => x.isSuccess && x.data?.franchiseNumber) && !wasNearbyUpdatedDueToUserProximity) {
            const { latitude: metroLat, longitude: metroLng } = location;

            const userDistanceToMetro = haversine({ longitude: geo.longitude, latitude: geo.latitude }, {
                latitude: metroLat,
                longitude: metroLng,
            });

            const twentyFiveMilesInMeters = 40234;
            const isWithinRadius = userDistanceToMetro < twentyFiveMilesInMeters;
            console.info(`User is ${(userDistanceToMetro * 0.0006214).toFixed(1)} mi from Metro`);

            if (isWithinRadius) {
                const newNearby = nearby.map(franchise => franchise.data);
                setNearbyToUse(newNearby);
                setWasNearbyUpdatedDueToUserProximity(true);
            }
        }
    }, [geo, nearby]);

    return (
        <CtaProvider hideFranchiseLink hideNationalCallCenter>
            <Layout>
                <Seo
                    title={seo?.meta_title || title}
                    description={seo?.meta_description}
                    imageUrl={seo?.meta_image?.url}
                />
                <SplitHero {...hero} />
                {franchises.length > 0 && (
                    <div css={{
                        ...paddingDefault(),
                        position: 'relative',
                        zIndex: 10,
                        backgroundColor: colors.grey100,
                    }}
                    >
                        <Container>
                            <Row>
                                <Col breakpoints={{
                                    dt: { span: 4, start: 8 },
                                    tb: { span: 12, start: 0 },
                                }}
                                >
                                    <aside>
                                        <div
                                            css={{
                                                width: '100%',
                                                maxWidth: '465px',
                                                paddingTop: '2.5rem',
                                                paddingBottom: '3rem',
                                                color: colors.shadesBlack,
                                            }}
                                        >
                                            <div css={{
                                                paddingBottom: '12px',
                                            }}
                                            >
                                                <Typography
                                                    color="primaryOrange"
                                                    fontSize="2xl"
                                                    fontWeight="medium"
                                                    css={{
                                                        paddingBottom: '12px',
                                                    }}
                                                >
                                                    Nearby Locations
                                                </Typography>
                                                <Divider />
                                            </div>
                                            <FadeTransition shouldChange={JSON.stringify(wasNearbyUpdatedDueToUserProximity)}>
                                                {nearbyToUse.map(franchise => (
                                                    <NearestItem
                                                        {...franchise}
                                                    />
                                                ))}
                                            </FadeTransition>
                                            <RowSet
                                                breakpoints={{ dt: { between: 18 } }}
                                                css={{
                                                    textAlign: 'center',
                                                    width: 'unset',
                                                    paddingTop: '16px',
                                                }}
                                            >
                                                <div>
                                                    Need help elsewhere?
                                                    {' '}
                                                    <div>
                                                        <StyledPillButtonAsLink
                                                            className="change-location-open-cta"
                                                            to="/locations"
                                                        >
                                                            {CHANGE_LOCATION}
                                                            {' '}
                                                            ›
                                                        </StyledPillButtonAsLink>
                                                    </div>
                                                </div>
                                            </RowSet>
                                        </div>
                                    </aside>
                                </Col>
                                <Col breakpoints={{
                                    dt: { span: 7 },
                                    tb: { span: 12, start: 0 },
                                }}
                                >
                                    <div
                                        css={RichTextEditorStyles}
                                        dangerouslySetInnerHTML={{ __html: page_content.copy }}
                                    />
                                </Col>
                            </Row>
                        </Container>
                    </div>
                )}
                <Blogroll data={blogroll} />
            </Layout>
        </CtaProvider>
    );
};

export const query = graphql`
    query ($slug: String!, $stateUrl: String!) {
        cmsMetroPage(url: {eq: $slug} state: {elemMatch: {url: {eq: $stateUrl}}}) {
            location {
                city
                state
                country
                latitude
                longitude
            }
            city
            state {
                title
                url
            }
            franchises {
                ...FranchiseMicrositeData
            }
            title
            url
            downtown_page
            franchise_number
            seo {
                meta_title
                meta_description
                meta_image {
                    url
                }
            }
            hero {
                fullwidth_background
                heading
                subheading
                copy
                button_link {
                    href
                    title
                }
                image {
                    filename
                    title
                    url
                    contentStackAsset(width: 1440)
                }
            }
            page_content {
                copy
            }
        }
    }
`;

export default MetroPage;
